import {
  compose,
  converge,
  anyPass,
  prop,
  length,
  __,
  not,
  filter,
  gt,
  equals,
  path,
  head,
  indexBy,
  always,
  cond,
  or,
  propOr,
} from 'ramda';
import { createSelector } from 'reselect';
import { parse } from 'iso8601-duration';

import { fromProps } from 'app/selectors/props';
import { isDirectToPay } from 'app/features/user/selectors';

const productsState = prop('products');

const products = compose(prop('products'), productsState);

const productsBySku = compose(indexBy(prop('sku')), products);

const productsLength = compose(prop('length'), products);

const paidProducts = compose(filter(compose(not, prop('isFree'))), products);

const firstPaidProductSku = compose(prop('sku'), head, paidProducts);

const isMultiTier = compose(gt(__, 1), length, paidProducts);

const getSelectedSku = compose(prop('selectedSKU'), productsState);

const isSkipFreeTrial = compose(prop('isSkipFreeTrial'), productsState);

const freeTrialDuration = compose(
  propOr('P7D', 'freeTrialDuration'),
  productsState,
);

const freeTrialDurationDays = compose(prop('days'), parse, freeTrialDuration);

const freeTrialDurationWeeks = compose(
  days => Math.floor(days / 7),
  freeTrialDurationDays,
);

const lengthOfFreetrial = compose(
  days => `${days} days`,
  freeTrialDurationDays,
);

const hasNoSkuSelected = compose(not, getSelectedSku);

const selectedProduct = converge(prop, [getSelectedSku, productsBySku]);

const selectedProductPlansToShowOnBilling = compose(
  prop('plansToShowOnBilling'),
  selectedProduct,
);

const productByPropsSku = converge(prop, [fromProps('sku'), productsBySku]);

const hasOneBillingPlan = compose(
  equals(1),
  path(['plansToShowOnBilling', 'length']),
);

const isProductWithOnePlan = compose(hasOneBillingPlan, productByPropsSku);

const isSelectedProductWithOnePlan = compose(
  hasOneBillingPlan,
  selectedProduct,
);

const productPlanSku = compose(prop('planSku'), productByPropsSku);

const productBenefits = compose(prop('benefits'), productByPropsSku);
const isBestDealProduct = compose(prop('isBestDeal'), productByPropsSku);
const isMostPopularProduct = compose(prop('isMostPopular'), productByPropsSku);
const hasSavingsTextKey = compose(prop('savingsSummaryKey'), productByPropsSku);
const isFreeProduct = compose(propOr(false, 'isFree'), productByPropsSku);
const isEligibleToSkipFreeTrial = converge(compose(not, or), [
  isDirectToPay,
  isFreeProduct,
]);
const hasSpecialTraits = anyPass([isBestDealProduct, isMostPopularProduct]);
const productHighlightTextKey = cond([
  [isBestDealProduct, always('landing_product_best_deal')],
  [isMostPopularProduct, always('landing_product_most_popular')],
  [hasSavingsTextKey, compose(prop('savingsSummaryKey'), productByPropsSku)],
]);
const productDurationKey = compose(prop('durationKey'), productByPropsSku);
const productBillingPeriodKey = compose(
  prop('billingPeriodKey'),
  productByPropsSku,
);
const productTitleKey = compose(
  prop('subscriptionTitleKey'),
  productByPropsSku,
);
const prevProductTitleKey = compose(
  prop('prevProductTitleKey'),
  productByPropsSku,
);
const productButtonTextKey = createSelector(
  [isDirectToPay, productByPropsSku],
  (isDirectToPay, product) =>
    isDirectToPay
      ? prop('directToPayButtonKey', product)
      : prop('freeTrialButtonKey', product),
);
const productOrderMobile = compose(prop('mobileOrder'), productByPropsSku);
const productOrderIpad = compose(prop('tabletOrder'), productByPropsSku);
const productOrderDesktop = compose(prop('desktopOrder'), productByPropsSku);

export {
  isMultiTier,
  products,
  productsLength,
  getSelectedSku,
  hasNoSkuSelected,
  selectedProduct,
  selectedProductPlansToShowOnBilling,
  productByPropsSku,
  isProductWithOnePlan,
  isSelectedProductWithOnePlan,
  firstPaidProductSku,
  productPlanSku,
  productBenefits,
  hasSavingsTextKey,
  isBestDealProduct,
  isMostPopularProduct,
  hasSpecialTraits,
  isFreeProduct,
  productDurationKey,
  productBillingPeriodKey,
  productTitleKey,
  prevProductTitleKey,
  productOrderMobile,
  productOrderIpad,
  productOrderDesktop,
  productButtonTextKey,
  productHighlightTextKey,
  isSkipFreeTrial,
  isEligibleToSkipFreeTrial,
  freeTrialDurationDays,
  freeTrialDurationWeeks,
  lengthOfFreetrial,
};
